import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { ArrowLeft, Car, User, Clock, MapPin, CreditCard, Phone, FileText } from 'lucide-react';
import { vehiclesAPI, handleApiError } from '../services/api';
import { useFlashMessage } from '../components/FlashMessage';
import { useAuth } from '../context/AuthContext';
import LoadingSpinner from '../components/LoadingSpinner';
import { formatPhoneNumber } from '../utils/phoneFormatter';

const OwnerVehicleDetails = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const { user } = useAuth();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);
  const { showDanger, FlashContainer } = useFlashMessage();

  // Owner yetkisi kontrolü
  const hasAccess = user && (
    user.isOwner ||
    user.user_type === 'owner'
  );

  useEffect(() => {
    if (hasAccess) {
      loadVehicle();
    }
  }, [id, hasAccess]);

  const loadVehicle = async () => {
    setLoading(true);
    try {
      // Kullanıcının lokasyonlarından araçları getir
      if (user?.locations && user.locations.length > 0) {
        for (const location of user.locations) {
          try {
            const response = await vehiclesAPI.getActiveVehicles(location.id);
            if (response.data.success) {
              const foundVehicle = response.data.data.find(v => v.id === parseInt(id));
              if (foundVehicle) {
                setVehicle(foundVehicle);
                setLoading(false);
                return;
              }
            }
          } catch (error) {
            console.warn(`Failed to load vehicle from location ${location.id}:`, error);
          }
        }

        // Aktif araçlarda bulunamadıysa teslim edilmiş araçlarda ara
        for (const location of user.locations) {
          try {
            const response = await vehiclesAPI.getDeliveredVehicles(location.id);
            if (response.data.success) {
              const foundVehicle = response.data.data.find(v => v.id === parseInt(id));
              if (foundVehicle) {
                setVehicle(foundVehicle);
                setLoading(false);
                return;
              }
            }
          } catch (error) {
            console.warn(`Failed to load delivered vehicle from location ${location.id}:`, error);
          }
        }
      }

      showDanger('Araç bulunamadı.');
      navigate('/owner/vehicles');
    } catch (error) {
      console.error('Failed to load vehicle:', error);
      showDanger('Araç yüklenirken hata oluştu: ' + handleApiError(error));
      navigate('/owner/vehicles');
    } finally {
      setLoading(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      received: { label: 'Teslim Alındı', color: 'bg-blue-100 text-blue-800' },
      washing: { label: 'Yıkanıyor', color: 'bg-yellow-100 text-yellow-800' },
      parked: { label: 'Park Edildi', color: 'bg-purple-100 text-purple-800' },
      ready: { label: 'Hazır', color: 'bg-green-100 text-green-800' },
      called: { label: 'Çağrıldı', color: 'bg-orange-100 text-orange-800' },
      delivered: { label: 'Teslim Edildi', color: 'bg-gray-100 text-gray-800' }
    };

    const statusInfo = statusMap[status] || { label: status, color: 'bg-gray-100 text-gray-800' };

    return (
      <span className={`inline-flex items-center px-4 py-2 rounded-lg font-semibold text-sm ${statusInfo.color}`}>
        {statusInfo.label}
      </span>
    );
  };

  const getServiceTypeText = (type) => {
    switch (type) {
      case 'both': return 'Vale + Yıkama';
      case 'wash': return 'Oto Yıkama';
      case 'valet': return 'Vale Hizmeti';
      default: return type;
    }
  };

  if (!hasAccess) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Bu sayfaya erişim yetkiniz bulunmamaktadır.</p>
        <p className="text-gray-500 text-sm">Kullanıcı türü: {user?.user_type}</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex items-center justify-center py-8">
        <LoadingSpinner size="lg" />
        <span className="ml-3 text-gray-600">Araç bilgileri yükleniyor...</span>
      </div>
    );
  }

  if (!vehicle) {
    return (
      <div className="text-center py-8">
        <p className="text-red-600">Araç bulunamadı.</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <FlashContainer />

      {/* Header */}
      <div className="flex items-center justify-between">
        <button
          onClick={() => navigate('/owner/vehicles')}
          className="flex items-center text-gray-600 hover:text-gray-900"
        >
          <ArrowLeft className="w-5 h-5 mr-2" />
          Araçlara Dön
        </button>
      </div>

      {/* Vehicle Info Card */}
      <div className="card">
        <div className="card-header bg-gradient-to-r from-[#7a2c2c] to-[#5a1f1f]">
          <h2 className="text-lg font-semibold text-white">Araç Detayları</h2>
        </div>
        <div className="card-body space-y-8">
          {/* Vehicle Details - Emphasized */}
          <div className="bg-gradient-to-br from-red-50 to-orange-50 border-2 border-red-200 rounded-xl p-6 shadow-sm">
            <div className="flex items-center justify-between mb-6">
              <div className="flex items-center">
                <div className="h-16 w-16 bg-gradient-to-br from-[#7a2c2c] to-[#5a1f1f] rounded-full flex items-center justify-center shadow-md">
                  <Car className="h-8 w-8 text-white" />
                </div>
                <div className="ml-4">
                  <div className="text-xl font-bold text-gray-900">{vehicle.plate_number}</div>
                  <div className="text-sm text-gray-600 font-medium">{vehicle.brand} {vehicle.model}</div>
                </div>
              </div>
              {vehicle.reference_number && (
                <div className="bg-[#7a2c2c] text-white px-4 py-2 rounded-lg font-bold text-lg shadow-md">
                  CS{vehicle.reference_number}
                </div>
              )}
            </div>

            {/* Current Status */}
            <div className="mb-6">
              <div className="text-xs text-gray-500 mb-2">Mevcut Durum</div>
              {getStatusBadge(vehicle.status)}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Müşteri - Sadece gerçek müşteri adı varsa göster */}
              {vehicle.customer_name && vehicle.customer_name !== 'Veribil' && (
                <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                  <User className="h-5 w-5 text-[#7a2c2c] mr-3 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Müşteri</div>
                    <span className="text-sm font-semibold text-gray-900">{vehicle.customer_name}</span>
                  </div>
                </div>
              )}

              {/* Telefon - Sadece gerçek telefon varsa göster */}
              {vehicle.customer_phone && vehicle.customer_phone !== '00000000000' && (
                <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                  <Phone className="h-5 w-5 text-[#7a2c2c] mr-3 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Telefon</div>
                    <span className="text-sm font-semibold text-gray-900">
                      {formatPhoneNumber(vehicle.customer_phone)}
                    </span>
                  </div>
                </div>
              )}

              {/* Giriş Zamanı - Her zaman göster */}
              <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                <Clock className="h-5 w-5 text-orange-500 mr-3 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Giriş Zamanı</div>
                  <span className="text-sm font-semibold text-gray-900">{new Date(vehicle.check_in).toLocaleString('tr-TR')}</span>
                </div>
              </div>

              {/* Çıkış Zamanı - Sadece teslim edilmişse göster */}
              {vehicle.check_out && (
                <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                  <Clock className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Çıkış Zamanı</div>
                    <span className="text-sm font-semibold text-gray-900">{new Date(vehicle.check_out).toLocaleString('tr-TR')}</span>
                  </div>
                </div>
              )}

              {/* Hizmet Türü - Her zaman göster */}
              <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                <Car className="h-5 w-5 text-purple-500 mr-3 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Hizmet Türü</div>
                  <span className="text-sm font-semibold text-gray-900">{getServiceTypeText(vehicle.service_type)}</span>
                </div>
              </div>

              {/* Tutar - Her zaman göster */}
              <div className="flex items-center bg-white rounded-lg p-3 shadow-sm">
                <CreditCard className="h-5 w-5 text-green-500 mr-3 flex-shrink-0" />
                <div>
                  <div className="text-xs text-gray-500">Tutar</div>
                  <span className="text-sm font-semibold text-gray-900">₺{parseFloat(vehicle.actual_amount || 0).toFixed(0)}</span>
                </div>
              </div>

              {/* Park Yeri - Sadece belirtilmişse göster */}
              {vehicle.parking_spot && (
                <div className="flex items-center md:col-span-2 bg-white rounded-lg p-3 shadow-sm">
                  <MapPin className="h-5 w-5 text-red-500 mr-3 flex-shrink-0" />
                  <div>
                    <div className="text-xs text-gray-500">Park Yeri</div>
                    <span className="text-sm font-semibold text-gray-900">{vehicle.parking_spot}</span>
                  </div>
                </div>
              )}

              {/* Not - Sadece varsa göster */}
              {vehicle.parking_note && (
                <div className="flex items-start md:col-span-2 bg-yellow-50 border border-yellow-200 rounded-lg p-3 shadow-sm">
                  <div className="flex-shrink-0 mr-3 mt-0.5">
                    <FileText className="w-5 h-5 text-yellow-600" />
                  </div>
                  <div className="flex-1">
                    <div className="text-xs text-yellow-700 font-semibold mb-1">Not</div>
                    <span className="text-sm text-gray-900">{vehicle.parking_note}</span>
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Info Message */}
          <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-sm text-gray-800">
            <strong className="text-[#7a2c2c]">Bilgi:</strong> Araç durumunu güncellemek için lütfen personel ile iletişime geçin.
          </div>
        </div>
      </div>
    </div>
  );
};

export default OwnerVehicleDetails;
